import { CurrencyPipe, DatePipe, PercentPipe } from '@angular/common';
import { Column, GridRow } from '@shared';
import { IChangeset } from '@shared-data';
import { v4 as uuidv4 } from 'uuid';
import { IPricingItem } from './pricing-item.interface';

class UpchargeChangeset implements IChangeset, IPricingItem {
  id: string;
  restaurantId: number;
  restaurantNo: string;
  componentId: number;

  categoryId: string;
  status?: string;

  @Column({
    field: 'size',
    title: 'Size',
    order: 2.5,
    width: 150,
    filterable: true,
    filterType: 'text',
    type: 'string',
    fixed: true,
    sortable: true,
    visibleInQuickView: true,
    align: 'left',
  })
  size: string;

  @Column({
    field: 'daypart',
    title: 'Daypart',
    order: 2.6,
    width: 150,
    filterable: true,
    filterType: 'text',
    type: 'string',
    sortable: true,
    visibleInQuickView: true,
    align: 'left',
  })
  daypart: string;

  @Column({
    field: 'parItemId',
    title: 'Brink ID',
    order: 2,
    width: 150,
    filterable: true,
    filterType: 'custom',
    type: 'number',
    fixed: true,
    sortable: true,
    visibleInQuickView: true,
  })
  parItemId: number;

  @Column({
    field: 'plu',
    title: 'PLU',
    order: 2,
    width: 150,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    fixed: true,
    sortable: true,
    visibleInQuickView: true,
  })
  plu?: string;

  @Column({
    field: 'itemName',
    title: 'Product',
    order: 1,
    width: 200,
    filterable: true,
    filterType: 'custom',
    type: 'string',
    fixed: true,
    sortable: true,
    visibleInQuickView: true,
    align: 'left',
  })
  itemName: string;

  @Column({
    field: 'itemPrice',
    title: 'Current Upsize Price',
    order: 3,
    width: 150,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    fixed: true,
    sortable: true,
    visibleInQuickView: true,
  })
  itemPrice: number;

  @Column({
    field: 'deliveryPrice',
    title: 'Current Upsize Delivery Price',
    order: 4,
    width: 150,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    fixed: true,
    sortable: true,
    visibleInQuickView: true,
  })
  deliveryPrice?: number;

  @Column({
    field: 'newPrice',
    title: 'New Upsize Price',
    order: 3,
    width: 150,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    fixed: true,
    sortable: true,
    editable: true,
    visibleInQuickView: true,
  })
  newPrice?: number;

  @Column({
    field: 'newDeliveryPrice',
    title: 'New Upsize Delivery Price',
    order: 4,
    width: 150,
    pipeToken: CurrencyPipe,
    filterable: true,
    filterType: 'numeric',
    type: 'number',
    fixed: true,
    sortable: true,
    editable: true,
    visibleInQuickView: true,
  })
  newDeliveryPrice?: number;

  pricingScheduledDate: Date | null;

  availabilityScheduledDate: Date | null;

  upchargeCategory: string;
  type: string = 'pricing';

  @Column({
    field: 'deliveryUpchargePercentage',
    title: 'Delivery Upcharge %',
    order: 11,
    pipeToken: PercentPipe,
    filterable: true,
    filterType: 'percentage',
    sortable: true,
    width: 90,
    hidden: true,
  })
  deliveryUpchargePercentage?: number;

  @Column({
    field: 'lastModPricingInstore',
    hidden: true,
    title: 'Last Modified Pricing In-Store',
    order: 12,
    pipeToken: DatePipe,
    pipeArgs: ['MM/dd/yyyy hh:mm a'],
    type: 'date',
    width: 170,
    filterType: 'date',
    filterable: true,
  })
  lastModPricingInstore?: Date;

  @Column({
    field: 'lastModPricingDelivery',
    hidden: true,
    title: 'Last Modified Pricing Delivery',
    order: 13,
    pipeToken: DatePipe,
    pipeArgs: ['MM/dd/yyyy hh:mm a'],
    type: 'date',
    width: 170,
    filterType: 'date',
    filterable: true,
  })
  lastModPricingDelivery?: Date;

  isDeliveryAvailable?: boolean;


  @Column({
    field: 'isActive',
    title: 'Is Active',
    order: 1.2,
    width: 80,
    filterable: true,
    filterType: 'boolean',
    type: 'boolean',
    sortable: true,
    visibleInQuickView: false,
    filterOptions: [
      { name: 'Yes', value: true },
      { name: 'No', value: false },
    ],
  })
  isActive?: boolean;

  constructor(changeset?: Partial<UpchargeChangeset>) {
    this.id = uuidv4();
    this.restaurantId = changeset?.restaurantId ?? 0;
    this.restaurantNo = changeset?.restaurantNo ?? '';
    this.itemName = changeset?.itemName ?? '';
    this.itemPrice = changeset?.itemPrice ?? 0;
    this.deliveryPrice = changeset?.deliveryPrice;
    this.newPrice = changeset?.newPrice;
    this.newDeliveryPrice = changeset?.newDeliveryPrice;
    this.upchargeCategory = changeset?.upchargeCategory ?? '';
    this.categoryId = changeset?.categoryId ?? '';
    this.size = changeset?.size ?? '';
    this.parItemId = changeset?.parItemId ?? 0;
    this.plu = changeset?.plu;
    this.daypart = changeset?.daypart ?? '';
    this.availabilityScheduledDate =
      changeset?.availabilityScheduledDate ?? null;
    this.pricingScheduledDate = changeset?.pricingScheduledDate ?? null;
    this.componentId = changeset?.componentId ?? 0;
    this.deliveryUpchargePercentage = changeset?.deliveryUpchargePercentage;
    this.status = changeset?.status;
    this.lastModPricingInstore = changeset?.lastModPricingInstore;
    this.lastModPricingDelivery = changeset?.lastModPricingDelivery;
    this.isDeliveryAvailable = changeset?.isDeliveryAvailable;
    this.isActive = changeset?.isActive;
  }
}

export class GridRowUpchargePricingChangeset extends GridRow(
  UpchargeChangeset,
) {}
